import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi } from '../api/authApi';
import { useAuth } from './AuthContext';

const OrderPresetsContext = createContext(null);
const STORAGE_KEY = 'lt-order-presets';

export const DEFAULT_PRESETS = [
  {
    id: 'preset-mkt-10',
    name: 'Market 10',
    orderBy: 'qty',
    qty: 10,
    notional: null,
    orderType: 'market',
    timeInForce: 'day',
    limitOffset: null,
    extendedHours: false,
  },
  {
    id: 'preset-lmt-mid-5c',
    name: 'Limit mid−0.05',
    orderBy: 'qty',
    qty: 10,
    notional: null,
    orderType: 'limit',
    timeInForce: 'day',
    limitOffset: -0.05,
    extendedHours: false,
  },
  {
    id: 'preset-ext-mkt',
    name: 'Ext hours mkt',
    orderBy: 'qty',
    qty: 5,
    notional: null,
    orderType: 'market',
    timeInForce: 'day',
    limitOffset: null,
    extendedHours: true,
  },
];

const defaultState = () => ({
  activeId: DEFAULT_PRESETS[0].id,
  presets: DEFAULT_PRESETS.map((p) => ({ ...p, updatedAt: new Date().toISOString() })),
});

const loadLocal = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return defaultState();
    const presets = Array.isArray(raw.presets) && raw.presets.length ? raw.presets : defaultState().presets;
    const activeId =
      raw.activeId && presets.some((p) => p.id === raw.activeId)
        ? raw.activeId
        : presets[0]?.id || null;
    return { activeId, presets };
  } catch {
    return defaultState();
  }
};

const persistLocal = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
};

export function midFromQuote(quote) {
  if (!quote) return null;
  const bid = Number(quote.bidPrice ?? quote.bp);
  const ask = Number(quote.askPrice ?? quote.ap);
  if (Number.isFinite(bid) && Number.isFinite(ask)) return (bid + ask) / 2;
  if (Number.isFinite(ask)) return ask;
  if (Number.isFinite(bid)) return bid;
  return null;
}

/** Limit price = mid + offset (e.g. offset -0.05 → mid − $0.05). */
export function priceFromMidOffset(mid, offset) {
  const m = Number(mid);
  const o = offset == null || offset === '' ? 0 : Number(offset);
  if (!Number.isFinite(m)) return null;
  const off = Number.isFinite(o) ? o : 0;
  return Math.max(0.01, Number((m + off).toFixed(4)));
}

/**
 * Build Alpaca order payload from a preset + runtime fields.
 */
export function buildOrderFromPreset(preset, { symbol, side, qty, notional, quote, currentPrice } = {}) {
  const p = preset || {};
  const order = {
    symbol: String(symbol || '').toUpperCase(),
    side,
    type: p.orderType || 'market',
    timeInForce: p.timeInForce || 'day',
  };

  const orderBy = p.orderBy || 'qty';
  if (orderBy === 'notional') {
    const n = notional != null ? Number(notional) : Number(p.notional);
    if (!Number.isFinite(n) || n <= 0) throw new Error('Preset notional is required');
    order.notional = n;
  } else {
    const q = qty != null ? Number(qty) : Number(p.qty);
    if (!Number.isFinite(q) || q <= 0) throw new Error('Preset quantity is required');
    order.qty = q;
  }

  if (order.type === 'limit' || order.type === 'stop_limit') {
    const mid = midFromQuote(quote) ?? (Number.isFinite(Number(currentPrice)) ? Number(currentPrice) : null);
    const limitPrice = priceFromMidOffset(mid, p.limitOffset);
    if (limitPrice == null) throw new Error('Need a quote mid to apply limit offset');
    order.limitPrice = limitPrice;
  }

  if (p.extendedHours) {
    order.extendedHours = true;
    if (order.timeInForce === 'day' && (order.type === 'limit' || order.type === 'stop_limit')) {
      // Alpaca extended hours typically requires day + limit
    }
  }

  return order;
}

export const OrderPresetsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState(() => loadLocal());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHydrated(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await authApi.getPreferences();
        const remote = response.data?.orderPresets;
        if (!cancelled && remote && typeof remote === 'object' && Array.isArray(remote.presets)) {
          const next = {
            activeId: remote.activeId || remote.presets[0]?.id || null,
            presets: remote.presets.length ? remote.presets : defaultState().presets,
          };
          setState(next);
          persistLocal(next);
        }
      } catch {
        // keep local
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    persistLocal(state);
    if (!hydrated || !isAuthenticated) return undefined;
    const timer = setTimeout(() => {
      authApi.savePreferences({ orderPresets: state }).catch(() => {});
    }, 450);
    return () => clearTimeout(timer);
  }, [state, hydrated, isAuthenticated]);

  const activePreset = useMemo(
    () => state.presets.find((p) => p.id === state.activeId) || state.presets[0] || null,
    [state]
  );

  const setActiveId = useCallback((id) => {
    setState((prev) => ({ ...prev, activeId: id }));
  }, []);

  const savePreset = useCallback((preset) => {
    setState((prev) => {
      const id = preset.id || `preset-${Date.now()}`;
      const nextPreset = {
        ...preset,
        id,
        updatedAt: new Date().toISOString(),
      };
      const exists = prev.presets.some((p) => p.id === id);
      const presets = exists
        ? prev.presets.map((p) => (p.id === id ? nextPreset : p))
        : [nextPreset, ...prev.presets].slice(0, 40);
      return {
        activeId: id,
        presets,
      };
    });
  }, []);

  const removePreset = useCallback((id) => {
    setState((prev) => {
      const presets = prev.presets.filter((p) => p.id !== id);
      const activeId =
        prev.activeId === id ? presets[0]?.id || null : prev.activeId;
      return { activeId, presets };
    });
  }, []);

  const value = useMemo(
    () => ({
      presets: state.presets,
      activeId: state.activeId,
      activePreset,
      setActiveId,
      savePreset,
      removePreset,
      buildOrderFromPreset,
      priceFromMidOffset,
      midFromQuote,
    }),
    [state, activePreset, setActiveId, savePreset, removePreset]
  );

  return (
    <OrderPresetsContext.Provider value={value}>{children}</OrderPresetsContext.Provider>
  );
};

export const useOrderPresets = () => {
  const ctx = useContext(OrderPresetsContext);
  if (!ctx) throw new Error('useOrderPresets must be used within OrderPresetsProvider');
  return ctx;
};
